import { format, isFirstDayOfMonth, parseISO, isSameMonth, startOfMonth, endOfMonth } from 'date-fns';

const dateConfig = (option: number) => {
    switch (option) {
        case 1:
            return 'MMM d';
        case 2:
            return 'd MMM, yyyy';
        case 3:
            return 'MMM d, yyyy · HH:mm';
        case 4:
            return 'MMM, yyyy';
        default:
            return 'MMM d';
    }
};

const timeConfig = (option: number) => {
    switch (option) {
        case 1:
            return 'HH:mm';
        default:
            return 'HH:mm';
    }
};

const formatDate = (date?: Date, option: number = 1) => {
    if (!date) return '';

    return format(date, dateConfig(option));
};

const formatTime = (date?: Date, option: number = 1) => {
    if (!date) return '';

    return format(date, timeConfig(option));
};

const getDateOnly = (date?: Date | null) => {
    return date && format(date, 'yyyy-MM-dd');
};

// Defensive caps so a pathological or cyclic response body cannot blow the
// JS call stack inside axios `transformResponse` and surface as
// `RangeError: Maximum call stack size exceeded` in Sentry.
const PARSE_DATES_MAX_DEPTH = 100;

function parseDatesInternal(obj: any, depth: number, seen: WeakSet<object>): any {
    if (depth > PARSE_DATES_MAX_DEPTH) {
        return obj;
    }

    if (Array.isArray(obj)) {
        if (seen.has(obj)) return obj;
        seen.add(obj);
        return obj.map((item) => parseDatesInternal(item, depth + 1, seen));
    }

    if (obj && typeof obj === 'object') {
        if (seen.has(obj)) return obj;
        seen.add(obj);
        return Object.keys(obj).reduce((acc, key) => {
            const value = obj[key];
            acc[key] = isISODateString(value)
                ? new Date(value)
                : parseDatesInternal(value, depth + 1, seen);
            return acc;
        }, {} as any);
    }

    return obj;
}

function parseDates(obj: any): any {
    return parseDatesInternal(obj, 0, new WeakSet<object>());
}

function isISODateString(value: any): boolean {
    // Check if the value is a string and matches ISO 8601 date format
    return (
        typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z$/.test(value)
    );
}

const sleep = (ms: number) => {
    return new Promise((resolve) => setTimeout(resolve, ms));
};

const shouldRenderBookingInMonth = (
    selectedMonth: Date | string,
    checkIn: Date | string,
    checkOut: Date | string,
) => {
    const monthDate = typeof selectedMonth === 'string' ? parseISO(selectedMonth) : selectedMonth;

    const inDate = typeof checkIn === 'string' ? parseISO(checkIn) : checkIn;

    const outDate = typeof checkOut === 'string' ? parseISO(checkOut) : checkOut;

    if (isFirstDayOfMonth(outDate) && isSameMonth(outDate, monthDate)) {
        return false;
    }

    const monthStart = startOfMonth(monthDate);
    const monthEnd = endOfMonth(monthDate);
    return inDate <= monthEnd && outDate > monthStart;
};

export const DateTimeHelper = {
    formatDate,
    formatTime,
    parseDates,
    isISODateString,
    sleep,
    getDateOnly,
    shouldRenderBookingInMonth,
};
